import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { FormGroup, AbstractControl } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Subject, Observable } from 'rxjs';
import { DeviceBindingService } from '../../../services/device-binding.service';
import { LinkItem } from '../../../models/common';
import {
  AuthenticationService,
  UsernameBindingCredentials,
  PasscodeChallengeContainer,
} from '../../../services/authentication.service';
import { ErrorService } from '../../../services/error.service';
import { BrandService } from '../../../services/brand.service';
import { ThreatMetrixService } from '../../../services/threat-metrix.service';
import { BrandedRouter } from '../../../services/brandedRouter';
import { NonSCAStoreStaffCardsService } from '../../../services/strong-customer-authentication/non-SCA-store-staff-cards.service';
import { takeUntil } from 'rxjs/operators';
import { UsernameService } from './username.service';
import {
  CookieConsentService,
  CookiePreferences,
} from '../../../services/cookie-consent.service';
import { FeatureFlagsService } from '../../../services/feature-flags.service';
import { Feature } from '../../../enums/features';
import { LivePersonService } from '../../../services/live-person.service';

import { AnalyticsService } from '../../../services/analytics.service';
import {
  gtmEventTracking,
  GTMTracking,
} from '../../../enums/google-analytics-tracking';
import { IScaUsernameContent } from '../../../models/content';

@Component({
  selector: 'sca-username',
  templateUrl: './username.component.html',
})
export class UsernameComponent implements OnInit, OnDestroy {
  private fingerprintedSubject = new BehaviorSubject<string>(null);
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  @Input() usernameGroup: FormGroup;
  @Input() content: IScaUsernameContent;

  public GTMTracking = GTMTracking;
  usernameInputHidden: boolean = false;
  brand: string;
  forgotUsernameLink: LinkItem;
  forgotPasscodeLink: LinkItem;
  errors: Array<any> = [];
  userName: string;
  userNameControl: AbstractControl;
  cookiePreferences: Observable<CookiePreferences>;
  displayUserNameTooltip: boolean;

  @Output() isSubmittingChange = new EventEmitter<boolean>();
  @Output() usernameResponseChange =
    new EventEmitter<PasscodeChallengeContainer>();
  @Output() userNameChange = new EventEmitter<string>();

  constructor(
    private analyticsService: AnalyticsService,
    private authenticationService: AuthenticationService,
    private cookieService: CookieService,
    private brandService: BrandService,
    private threatMetrixService: ThreatMetrixService,
    private brandedRouter: BrandedRouter,
    private nonSCAStoreStaffCardsService: NonSCAStoreStaffCardsService,
    private errorService: ErrorService,
    private deviceBindingService: DeviceBindingService,
    private usernameService: UsernameService,
    private cookieConsentService: CookieConsentService,
    private featureFlagsService: FeatureFlagsService,
    private livePersonService: LivePersonService
  ) {
    this.brand = this.brandService.getBrand();
    this.featureFlagsService
      .hasFeatureFlag(
        Feature.DisplayUserNameTooltip,
        this.brandService.getBrand()
      )
      .then((result) => (this.displayUserNameTooltip = result));

    this.deviceBindingService.getDeviceFingerprint((deviceInfo) =>
      this.fingerprintedSubject.next(deviceInfo)
    );
  }

  ngOnInit(): void {
    this.initForm();
    this.generateLinks();
    this.userNameControl = this.usernameGroup.controls['userName'];

    this.cookiePreferences = this.cookieConsentService.cookiePreferences;
    this.cookieConsentService.checkCookieSettings();
    this.livePersonService.clearLivePersonErrorTracking();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  submit(): void {
    if (this.userNameControl.invalid) {
      this.analyticsService.pushToDataLayer(
        gtmEventTracking[GTMTracking.LoginUsernameRequirementError]
      );
      return;
    }

    this.isSubmittingChange.emit(true);

    const username = this.userNameControl.value.trim();
    this.usernameService.passUsername(username);
    this.saveRememberMeCookie(username);
    this.fingerprintedSubject
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((deviceInfo) => {
        if (deviceInfo) this.submitUsernameWithDeviceInfo(deviceInfo, username);
      });
  }

  private submitUsernameWithDeviceInfo(deviceInfo: string, username: string) {
    this.errorService.clear();

    const credentials: UsernameBindingCredentials = {
      username: username,
      deviceInfo: deviceInfo,
      tmxSessionId: this.threatMetrixService.getSessionId('Login'),
    };

    this.authenticationService.getPasscodeBindingChallenge(credentials).then(
      (result: PasscodeChallengeContainer) => {
        if (result.storeOrStaffProduct) {
          this.nonSCAStoreStaffCardsService.notifyIsNonScaLoginJourneyObservers();
          this.nonSCAStoreStaffCardsService.setUsername(username);
        } else {
          this.usernameResponseChange.emit(result);
          this.userNameChange.emit(username);
          this.isSubmittingChange.emit(false);
        }
      },
      (error) => {
        this.analyticsService.pushToDataLayer(
          gtmEventTracking[GTMTracking.LoginUsernameValidError]
        );
        this.errorService.processError(error);
        this.isSubmittingChange.emit(false);
      }
    );
  }

  fieldHasRequiredError() {
    return (
      this.userNameControl.invalid &&
      (this.userNameControl.dirty || this.userNameControl.touched) &&
      this.userNameControl.invalid
    );
  }

  fieldHasError() {
    return (
      this.userNameControl.invalid &&
      (this.userNameControl.dirty || this.userNameControl.touched)
    );
  }

  trackCtaActions(event: GTMTracking) {
    this.analyticsService.trackCtaActions(event);
  }

  private saveRememberMeCookie(username: string) {
    if (!this.usernameGroup.controls['rememberMe'].value) {
      return;
    }
    this.cookieService.set(
      this.brand + 'userName',
      username,
      new Date('2099-01-01T00:00:00Z'),
      '/',
      undefined,
      true
    );
  }

  private initForm() {
    let username: string;
    try {
      username = this.cookieService.get(this.brand + 'userName');
    } catch (exception) {
      username = '';
    }

    this.usernameGroup.controls['userName'].setValue(username);
  }

  private generateLinks() {
    this.forgotUsernameLink = new LinkItem(
      this.content.usernameInput.forgottenLinkLabel,
      this.brandedRouter.getBrandedLink('/recovery/retrieve-username'),
      false,
      'btnRetrieveUsername'
    );
  }
}
